import type { IncomeTaxOutput, Residency } from "./types.js";

// Plain-English result copy for the income-tax engine. Centralised in the rules
// package so every surface (Expo result screens, "my results", PDF reports)
// renders identical wording from one source — which matters for regulation, not
// just DRY. Every string here is INFORMATION, not advice (validation §3.1): it
// states what the tax/levy/repayment IS and how it derives from the ATO
// schedules, with no "save $X", no "you should", no product recommendation, no
// residency determination. Drift between channels is the information→advice
// kill-threat, so the copy lives here beside the rules it describes.

/** Whole-dollar AUD with thousands separators. Local to the copy layer so the
 * wording renders the same numbers everywhere, independent of any app's UI. */
export function formatAud(amount: number): string {
  return `A$${Math.round(amount).toLocaleString("en-AU")}`;
}

function residencyLabel(residency: Residency): string {
  switch (residency) {
    case "resident":
      return "Australian resident for tax purposes";
    case "foreign":
      return "foreign resident for tax purposes";
    case "whm":
      return "working holiday maker";
  }
}

export function takeHomeHeadline(out: IncomeTaxOutput): string {
  return `Your estimated take-home pay for the year: ${formatAud(out.takeHome)}`;
}

/** States the net-tax figure — never as a saving or a target. */
export function netTaxBullet(out: IncomeTaxOutput): string {
  return `On a taxable income of ${formatAud(out.taxableIncome)} as a ${residencyLabel(
    out.residency,
  )}, your estimated income tax is ${formatAud(out.incomeTax)} and your Medicare levy is ${formatAud(
    out.medicareLevy,
  )}.`;
}

/** Explains the tax-free threshold actually applied (full / part-year / none). */
export function thresholdBullet(out: IncomeTaxOutput): string {
  if (out.residency !== "resident") {
    return `As a ${residencyLabel(out.residency)} you do not receive the tax-free threshold — tax applies from the first dollar.`;
  }
  return `The tax-free threshold applied to your assessment is ${formatAud(out.taxFreeThreshold)}.`;
}

/** Describes the offsets applied, if any. Non-refundable, auto-applied framing. */
export function offsetBullet(out: IncomeTaxOutput): string | null {
  const parts: string[] = [];
  if (out.lito > 0) {
    parts.push(`a low income tax offset of ${formatAud(out.lito)}`);
  }
  if (out.sapto > 0) {
    parts.push(`a seniors and pensioners tax offset of ${formatAud(out.sapto)}`);
  }
  if (parts.length === 0) return null;
  return `Your assessment includes ${parts.join(" and ")}. These offsets are applied automatically when you lodge and can only reduce your income tax to ${formatAud(
    0,
  )} — they are not a separate payment.`;
}

/** Describes the compulsory study-loan repayment, if a loan was flagged. */
export function helpBullet(out: IncomeTaxOutput): string | null {
  if (out.helpRepayment <= 0) return null;
  return `Based on a repayment income of ${formatAud(out.helpRepaymentIncome)}, your estimated compulsory study and training support loan repayment for the year is ${formatAud(
    out.helpRepayment,
  )}. Under the marginal system this is charged only on income above the minimum repayment threshold.`;
}

/** Source-attribution + estimate framing. The ATO finalises at lodgement. */
export function sourceBullet(fy: string, verifiedAt: string): string {
  return `These figures are an estimate using the ATO income-tax rates, Medicare levy and study-loan thresholds for ${fy} (last verified ${verifiedAt}). The ATO works out the final amounts when you lodge your tax return — always check ato.gov.au.`;
}

export function explanationBullets(
  out: IncomeTaxOutput,
  fy: string,
  verifiedAt: string,
): string[] {
  const offset = offsetBullet(out);
  const help = helpBullet(out);
  return [
    netTaxBullet(out),
    thresholdBullet(out),
    ...(offset ? [offset] : []),
    ...(help ? [help] : []),
    sourceBullet(fy, verifiedAt),
  ];
}
